import { CalendarOptions, EventApi, EventInput, FullCalendarComponent } from "@fullcalendar/angular";
import dayGridPlugin from "@fullcalendar/angular/daygrid";
import interactionPlugin from "@fullcalendar/angular/interaction";
import deLocale from "fullcalendar/locales/de";
import themePlugin from "@fullcalendar/angular/themes/classic";
import timeGridPlugin from "@fullcalendar/angular/timegrid";
import { ResourceInput } from "@fullcalendar/angular-scheduler";
import resourceTimelinePlugin from "@fullcalendar/angular-scheduler/resource-timeline";
import { MenuItem } from "@openng/optimus-ui/api";
import { firstValueFrom } from "rxjs";
import { ComponentView } from "src/modules/app-template/models/component-view.model";
import { MainAppService } from "src/modules/app-template/services/main-app.service";
import { SmTimer } from "src/modules/sm-base/models/sm-timer.model";
import { GuiUtils } from "src/modules/utils/misc/gui-utils";
import { Arrays } from "src/modules/utils/shared/arrays";
import { Dates } from "src/modules/utils/shared/dates";
import { Map2 } from "src/modules/utils/shared/map2.model";
import { Objects } from "src/modules/utils/shared/objects";
import { Strings } from "src/modules/utils/shared/strings";
import { Utils } from "src/modules/utils/shared/utils";
import { EditCalendarEventAppointmentComponent } from "../components/edit-calendar-event-appointment/edit-calendar-event-appointment.component";
import { EditCalendarEventForProjectComponent } from "../components/edit-calendar-event-for-project/edit-calendar-event-for-project.component";
import { CalendarEventToolTipComponent } from "../components/calendar-event-tool-tip/calendar-event-tool-tip.component";
import { CalendarEvent } from "../netgen/CalendarEvent";
import { CalendarEventInstance } from "../netgen/CalendarEventInstance";
import { CalendarType } from "../netgen/CalendarType";
import { Project } from "../netgen/Project";
import { ProjectPlanningService } from "../services/project-planning.service";

export class FullCalendarWrapper {

    options: CalendarOptions;

    collapsedResourceKeys: string[] = [];
    resourcesById = new Map2<string, ResourceInput>();
    private alreadyRendered = false;
    private fc: FullCalendarComponent;
    private currentTippy: any = null;
    private eventElementsByCalendarEventId = new Map<number, Set<HTMLElement>>();
    private lastClickEventId: string | null = null;
    private lastClickTime = 0;
    private readonly dblClickDelayMs = 300;
    private knownProjectResourceIds = new Set<string>();
    private pendingCollapsedResourceKeys = new Set<string>();

    private startYear = new Date().getFullYear();
    private endYear = this.startYear + 1;
    
    constructor(
        public resourceView: boolean, 
        public projects: boolean, 
        public resourceType: "none" | "calendar" | "project",   
        public colorBy: "calendar" | "type", 
        public dataChanged: () => any,
        public view: ComponentView, 
        private app: MainAppService, 
        private service: ProjectPlanningService) {

        this.options = this.initOptions();
    }

    private initOptions(): CalendarOptions {
        return {
            timeZone: "local",
            plugins: [themePlugin, interactionPlugin, ...(this.resourceView ? [resourceTimelinePlugin] : [dayGridPlugin, timeGridPlugin])],
            initialView: this.resourceView ? "resourceTimeline" : "dayGridMonth",
            initialDate: new Date(),
            nowIndicator: true,
            scrollTime: "00:00",
            schedulerLicenseKey: "CC-Attribution-NonCommercial-NoDerivatives",
            firstDay: 1,
            locale: deLocale,
        // aspectRatio: 100, //Kleiner Hack, um zu verhindern, dass die Höhe zu groß wird und das Fenster scrollt
            displayEventTime: false,
            eventOverlap: false,
            eventMinHeight: 12,
            eventOrder: this.resourceView ? "portalOrder" : "",
            eventOrderStrict: true,
            weekNumbers: true,
            dayCellClass: info => this.getCalendarDateClassNames(info),
            dayHeaderClass: info => this.getCalendarDateClassNames(info),
            eventTitleClass: info => Arrays.itemsToString(Arrays.withoutNull(["pp-fullcal-event-title", Strings.split(info.event.className, " ").includes("event-parallel") ? "pp-event-parallel-title" : null]), " "),
            headerToolbar: { 
            left: "",
            center: "",
            right: ""
            },
            editable: true,            
            events: [
            ],
            selectable: true,
            dateClick: info => {
                const target = info.jsEvent.target as Element;
                if (this.isFullCalendarMenuBlockedTarget(target)) {
                    return;
                }
                this.openEmptySpaceContextMenu(info.date, this.resourceView ? info.resource?.id ?? this.getResourceIdFromCalendarElement(target) : null, info.jsEvent);
            },
            ...this.resourceView ? {
                visibleRange: {
                    start: this.startYear + "-01-01",
                    end: this.endYear + "-12-31"
                },
                slotDuration: { days: 1 },
                slotMinWidth: 45,
                slotHeaderInterval: { days: 1 },
                slotHeaderFormat: [
                    {
                        week: "short"
                    },
                    {
                        day: "2-digit",
                        month: "2-digit"
                    }
                ],
                slotHeaderAlign: "center",
                slotHeaderSticky: false,
                slotHeaderContent: arg =>
                {
                    if (arg.level === 1)
                    {
                        return Strings.removePrefix(arg.text, "KW ").padStart(2, "0") + "/" + (arg.date.getFullYear() % 100);
                    }

                    return arg.text;
                },
                slotLaneClass: info => this.getCalendarDateClassNames(info),
                slotHeaderClass: info => Arrays.itemsToString(Arrays.withoutNull([this.getCalendarDateClassNames(info), "pp-fullcal-slot-header"]), " "),
                resourceColumnHeaderContent: this.projects ? this.app.t("ProjectPlanning.contents") : this.app.t("entity.Calendar.entityName"),
                resourceColumnsWidth: this.projects ? 300 : 200,
                resourceOrder: "priority,title",
                resourceCellContent: arg => {
                    let resource = arg.resource;
                    if (resource.extendedProps?.isEvent) {
                        return {
                            html: "<span class=\"mr-2\"><b>" + Dates.format(resource.extendedProps.firstEvent.startDate as Date, "dd.mm") + "</b></span><span>" + resource.title + "</span>"
                        };
                    }
                    else {
                        return { 
                            html: resource.title 
                        };
                    }
                },
                resourceCellClass: arg => {
                    const r = arg.resource;

                    if (r.extendedProps?.isProject)
                    {
                        return Arrays.itemsToString(Arrays.withoutNull(["pp-fullcal-resource-cell", "resource-label-project", this.isFirstProjectResource(r) ? null : "pp-resource-separator"]), " ");
                    }
                    
                    else if (r.extendedProps?.isGroup)  {
                        return "pp-fullcal-resource-cell pp-resource-separator";
                    }
                    if (r.extendedProps?.isEvent)
                    {
                        return "pp-fullcal-resource-cell mouse-pointer";
                    }

                    return "pp-fullcal-resource-cell";
                },
                resourceLaneClass: arg =>
                {
                    const r = arg.resource;

                    if (r.extendedProps?.isProject) {
                        return Arrays.itemsToString(Arrays.withoutNull(["resource-row-project", this.isFirstProjectResource(r) ? null : "pp-resource-separator"]), " ");
                    }
                    else if (r.extendedProps?.isGroup)  {
                        return Arrays.itemsToString(Arrays.withoutNull(["resource-row-project", this.isFirstProjectResource(r) ? null : "pp-resource-separator"]), " ");
                    }

                    return "";
                },
           /*     resourceLabelDidMount: (info) => {
                    info.el.classList.add("pp-resource-separator");
                    info.el.addEventListener('click', () => {
                        this.scrollToResourceEvent(info.resource);
                    });
                },
                resourceLaneDidMount: (info: ResourceLaneMountArg) => {
                    info.el.classList.add("pp-resource-separator");
                },
             */   resources: [            
                ],
                headerToolbar: false
            } : {
                headerToolbar: false /*{
                    left: 'prev,next today',
                    center: 'title',
                    right: ''
                }*/
            },
            views: {
                timeGridWeek: {
                    titleFormat: args => {
                        const start = new Date(args.start.year, args.start.month, args.start.day);
                        const week = Dates.calendarWeek(start);
                        return "KW " + week + "/" + start.getFullYear();
                    }
                }
            },
            eventDidMount: info => {
                this.registerEventElement(info.event, info.el);
                info.el.addEventListener("mousedown", event => {
                    if (event.detail > 1) {
                        event.preventDefault();
                    }
                });
                info.el.addEventListener('contextmenu', (e: MouseEvent) => {
                    if (!e.ctrlKey) {
                        e.preventDefault();
                        e.stopPropagation();
                        this.openEventContextMenu(info.event);
                    }
                });
            },
            eventWillUnmount: info => {
                this.unregisterEventElement(info.event, info.el);
            },
            viewDidMount: info => {
                info.el.addEventListener("contextmenu", (event: MouseEvent) => this.openEmptySpaceContextMenuFromResourceLane(event));
                info.el.addEventListener("click", () => {
                    window.setTimeout(() => {
                        if (this.updateCollapsed()) {
                            this.updateResourceIds();
                        }
                    });
                });
            },
            eventClick: async info => {
                if (info.event.display == "background") {
                    return;
                }
              
                info.jsEvent.preventDefault();
                info.jsEvent.stopPropagation();

                const now = Date.now();
                const eventId = info.event.id;

                const isDoubleClick =  this.lastClickEventId === eventId && (now - this.lastClickTime) < this.dblClickDelayMs;

                this.lastClickEventId = eventId;
                this.lastClickTime = now;

                if (!isDoubleClick) {
                    return;
                }

                await this.service.editEvent(info.event.extendedProps.ce as CalendarEvent, this.dataChanged);
            },
            eventMouseEnter: async info => {
                if (info.event.display == "background") {
                    return;
                }
              
                this.highlightLinkedEvents(info.event);
                this.currentTippy?.destroy();
                this.currentTippy = null;

                this.currentTippy = await firstValueFrom(
                    this.service.tippyService.create(info.el, CalendarEventToolTipComponent, {
                        data: {
                            event: info.event.extendedProps.ce as CalendarEvent,
                            type: this.projects ? "projectView" : ""
                        },
                        variation: "tooltip",
                        delay: 1000,
                        placement: "auto"
                    })
                );

                Utils.setTimerOnce(1000, () => {
                    this.currentTippy?.show();
                });
            },
            eventMouseLeave: _ => {
                this.clearLinkedEventHighlights();
                this.currentTippy?.destroy();
                this.currentTippy = null;
            }
        };
    }


    private getCalendarDateClassNames(info: { date?: Date; isToday?: boolean }): string {
        let result: string[] = [];
        let day = info.date?.getDay();
        if (day === 6) {
            result.push("sm-calendar-weekend");
        }
        else if (day === 0) {
            result.push("sm-calendar-weekend");
        }

        if (info.isToday || this.isToday(info.date)) {
            result.push("sm-calendar-today");
        }

        return Arrays.itemsToString(result, " ");
    }

    private isToday(date: Date | undefined): boolean {
        if (date == null) {
            return false;
        }

        let today = new Date();
        return date.getFullYear() == today.getFullYear() && date.getMonth() == today.getMonth() && date.getDate() == today.getDate();
    }

    getResourcesById(result: Map2<string, ResourceInput>, input: ResourceInput[]): void {
        for (let r of input) {
            result.set(r.id, r);
            if (r.children != null) {
                this.getResourcesById(result, r.children);
            }
        }
    }

    private rawEventInput: CalendarEvent[];
    private rawResources: ResourceInput[];
    private rawDisplayedCalendarIds: number[];
    private initCalendarTimer: SmTimer;

    async initCalendar(fc: () => FullCalendarComponent, eventInput: CalendarEvent[], resources: ResourceInput[], displayedCalendarIds: number[]): Promise<void> {
        this.fc = fc();
        this.updateNewProjectResourcesCollapsedByDefault(resources);
        this.rawEventInput = eventInput;
        this.rawResources = resources;
        this.rawDisplayedCalendarIds = displayedCalendarIds;
        if (this.fc == null) {
            this.initCalendarTimer ??= new SmTimer("initCalendar", 1_000, 1_000, () => this.initCalendarInternal(fc));
        }
        else {
            await this.initCalendarInternalRun();
        }
    }

    private async initCalendarInternal(fc: () => FullCalendarComponent): Promise<void> {
        this.fc = fc();
        if (this.fc != null) {
            await this.initCalendarInternalRun();
        }
    }

    private async initCalendarInternalRun(): Promise<void> { 
        this.initCalendarTimer?.destroy();
        this.initCalendarTimer = null;
        await this.loadDataDependencies();
        
        this.options.resources = this.rawResources;
        this.resourcesById.clear();
        this.getResourcesById(this.resourcesById, this.rawResources);
        if (this.resourceType == "project") {
            for (let r of this.resourcesById.values()) {
                r.priority = Number.MAX_VALUE;
            }
        }

        let events: EventInput[] = Arrays.explode(this.rawEventInput, event => this.createCalendarEvents(event, this.rawDisplayedCalendarIds));

        this.updateEventOrder(events);

        await this.service.getHolidays();
        for (let holiday of this.service.holidays) {
            if (holiday.date.year >= this.startYear && holiday.date.year <= this.endYear) {
                events.push({
                    start: holiday.date.toDate(),
                    end: holiday.date.toDate(),
                    display: "background",
                    classNames: ["holiday"],
                    extendedProps: {
                        name: holiday.title
                    }
                })
            }
        }
    
        this.fc.events = events;

        let usedResourceIds = new Set<string>();
        for (let e of events) {
            if (e.resourceId != null) {
                usedResourceIds.add(e.resourceId);
            }
        }

        //Wird das noch benötigt? Es war wohl mal dazu da, um leere Ebenen auszufiltern - aber das verträgt sich nicht mit dem Auf- und Zuklappen
       /* if (this.resourceType == "project") {
            let filterResources = (input: ResourceInput[]): ResourceInput[] => {
                for (let r of input) {
                    if (r.children != null) {
                        r.children = filterResources(r.children); 
                    }
                }
                return input; //.filter(r => r.extendedProps?.isEvent || usedResourceIds.has(r.id));
            }
            
            this.options.resources = filterResources(this.options.resources);
        }
       */
        this.fc.getApi().render();
        this.applyPendingCollapsedResourceKeys();
           
        if (!this.alreadyRendered) {
            this.alreadyRendered = true;
            GuiUtils.angularTimerWithRetry(() => this.scrollToToday(), () => this.getScroller() != null, 100, 5, false, () => this.fc?.getApi() == null);
        }
    }

    private async loadDataDependencies(): Promise<void> {
        let dependencies: Promise<unknown>[] = [];
        if (this.colorBy == "type") {
            dependencies.push(this.service.getEventTypes());
        }
        if (this.colorBy == "calendar" || this.resourceType == "calendar") {
            dependencies.push(this.service.getCalendars(), this.service.getUsers());
        }
        if (this.colorBy == "calendar") {
            dependencies.push(this.service.getRoles(), this.service.getResources());
        }
        await Promise.all(dependencies);
    }


    private isFirstProjectResource(resource: ResourceInput): boolean {
        if (!resource.extendedProps?.isProject) {
            return false;
        }

        let projectResources = Array.from(this.resourcesById.values()).filter(r => r.extendedProps?.isProject);
        projectResources.sort((r1, r2) => {
            let result = Objects.cmp(r1.priority, r2.priority);
            return result != 0 ? result : Objects.cmp(r1.title, r2.title);
        });
        return projectResources[0]?.id == resource.id;
    }
    updateEventOrder(events: EventInput[]): void {
        if (this.resourceView) {
            for (let e of events) {
                e.className = Arrays.itemsToString(Strings.split(e.className as string ?? "", " ").filter(className => className != "event-parallel"), " ");
            }
            //Dieselbe Reihenfolge steuert sowohl die FullCalendar-Spuren als auch die Kennzeichnung paralleler Events
            events = Arrays.sort(events, (e1, e2) => {
                let result = Objects.cmp(e1.display == "background", e2.display == "background");
                if (result != 0) {
                    return result;
                }
                if (e1.display == "background" && e2.display == "background") {
                    return Objects.cmp(e1.start, e2.start);
                }

                let ce1 = e1.extendedProps.ce as CalendarEvent;
                let ce2 = e2.extendedProps.ce as CalendarEvent;
                
                result = Objects.cmp(ce1.getStartDateComparable(), ce2.getStartDateComparable());
                if (result != 0) {
                    return result;
                }
                result = -Objects.cmp(ce1.getDisplayEndDateComparable(), ce2.getDisplayEndDateComparable());
                if (result != 0) {
                    return result;
                }
                result = Objects.cmp(ce1.startDate, ce2.startDate);
                if (result != 0) {
                    return result;
                }
                result = -Objects.cmp(ce1.allDay, ce2.allDay);
                if (result != 0) {
                    return result;
                }
                result = Objects.cmp(e1.title, e2.title);
                if (result != 0) {
                    return result;
                }
                return Objects.cmp(e1.id, e2.id);
            });
            for (let index = 0; index < events.length; index++) {
                events[index].extendedProps.portalOrder = index;
            }
        
            let eventsByResource: { [key: string]: EventInput[] } = {};

            for (let e of events) {
                if (e.display == "background") {
                    continue;
                }
                let resourceId: string = e.resourceId;
                if (eventsByResource[resourceId] == null) {
                    eventsByResource[resourceId] = [];
                }
                eventsByResource[resourceId].push(e);
            }

            for (let resourceId in eventsByResource) {
                let rowEndTimes: string[] = [];
                for (let e of eventsByResource[resourceId]) {
                    let start = (e.extendedProps.ce as CalendarEvent).getStartDateComparable();
                    let end = (e.extendedProps.ce as CalendarEvent).getDisplayEndDateComparable();
            
                    let placed = false;
                    for (let rowIndex = 0; rowIndex < rowEndTimes.length; rowIndex++) {
                        if (start > rowEndTimes[rowIndex]) {
                            rowEndTimes[rowIndex] = end;
                            placed = true;
                            if (rowIndex > 0) {
                                e.className += " event-parallel";
                            }
                            break;
                        }
                    }

                    if (!placed) {
                        rowEndTimes.push(end);
                        if (rowEndTimes.length > 1) {
                            e.className += " event-parallel";
                        }
                    }
                }
            }
        }
    }

    updateCollapsed(): boolean {
        let ex = this.fc?.getApi()?.currentDataManager?.getCurrentData()?.resourceEntityExpansions;
        if (ex == null) {
            return false;
        }

        let collapsedResourceKeys = Objects.getPropertyNames(ex).filter(key => ex[key] === false);
        let result = !Arrays.equalsFlatUnordered(this.collapsedResourceKeys, collapsedResourceKeys);
        this.collapsedResourceKeys = collapsedResourceKeys;
        return result;
    }


    private updateNewProjectResourcesCollapsedByDefault(resources: ResourceInput[]): void {
        if (this.resourceType != "project") {
            return;
        }

        let resourceIds = new Set(this.getResourceIds(resources));
        let projectResourceIds = new Set(this.getProjectResourceIds(resources));
        this.collapsedResourceKeys = this.collapsedResourceKeys.filter(key => resourceIds.has(key));

        for (let id of projectResourceIds) {
            if (!this.knownProjectResourceIds.has(id)) {
                this.pendingCollapsedResourceKeys.add(id);
                if (!this.collapsedResourceKeys.includes(id)) {
                    this.collapsedResourceKeys.push(id);
                }
            }
        }

        for (let id of Array.from(this.pendingCollapsedResourceKeys)) {
            if (!resourceIds.has(id)) {
                this.pendingCollapsedResourceKeys.delete(id);
            }
        }

        this.knownProjectResourceIds = projectResourceIds;
    }

    private getResourceIds(resources: ResourceInput[]): string[] {
        return Arrays.explode(resources ?? [], resource => [
            Objects.toString(resource.id),
            ...this.getResourceIds(resource.children ?? [])
        ]);
    }

    private getProjectResourceIds(resources: ResourceInput[]): string[] {
        return Arrays.explode(resources ?? [], resource => [
            resource.extendedProps?.isProject ? Objects.toString(resource.id) : null,
            ...this.getProjectResourceIds(resource.children ?? [])
        ]).filter(id => id != null);
    }

    private applyPendingCollapsedResourceKeys(): void {
        if (this.pendingCollapsedResourceKeys.size == 0 || this.fc == null) {
            return;
        }

        let api = this.fc.getApi() as any;
        for (let id of Array.from(this.pendingCollapsedResourceKeys)) {
            api.dispatch({
                type: "SET_RESOURCE_ENTITY_EXPANDED",
                id,
                isExpanded: false
            });
            this.pendingCollapsedResourceKeys.delete(id);
        }
    }
    createCalendarEvents(e: CalendarEvent, displayedCalendarIds: number[]): EventInput[] {
        return e.instances.filter(instance => displayedCalendarIds == null || displayedCalendarIds.includes(instance.calendarId)).map(instance => {
            let bg = this.colorBy == "calendar" ? this.getCalendarColor(instance.calendarId) : this.service.eventTypes.find(et => et.id == e.typeId)?.color ?? 0;
            let color = Utils.toColorHexString(bg);
            let contrastColor = Utils.getTextColor(color);

            let result: EventInput = {
                id: Objects.toString(e.id) + "|" + instance.calendarId,
                allDay: e.allDay,
                start: e.getStartDateForFullCalendar(),
                end: e.getEndDateForFullCalendar(),
                title: e.title, //Strings.getSubstringCount(resourceId, "^") == 2 ? "" : e.title,
                display: "block",
                extendedProps: {
                    ce: e,
                    cei: instance,
                    resourceId: this.resourceType == "project" ? Objects.toString(e.projectId) + "|" + e.structurePath : null
                },
                color,
                contrastColor,
                className: Arrays.itemsToString(Arrays.withoutNull(["pp-fullcal-event", ...this.getLinkedEventClassNames(e), e.finished ? "task-finished" : null]), " ")
            };
            this.setEventResourceId(result);
            
            let res = this.resourcesById.get(result.resourceId);
            if (res != null && this.resourceType == "project") {
                res.priority = Math.min(res.priority as number, e.startDate.getTime());
            }

            return result;
        });
    }


    private getLinkedEventClassNames(e: CalendarEvent): string[] {
        return Arrays.withoutNull([
            e.getStartRelativeToEventId() != null ? "pp-linked-event-target" : null,
            this.rawEventInput?.some(e2 => e2.getStartRelativeToEventId() == e.id) ? "pp-linked-event-source" : null
        ]);
    }

    private registerEventElement(event: EventApi, element: HTMLElement): void {
        let calendarEvent = event.extendedProps.ce as CalendarEvent;
        if (calendarEvent?.id == null) {
            return;
        }

        let elements = this.eventElementsByCalendarEventId.get(calendarEvent.id);
        if (elements == null) {
            elements = new Set<HTMLElement>();
            this.eventElementsByCalendarEventId.set(calendarEvent.id, elements);
        }
        elements.add(element);
    }

    private unregisterEventElement(event: EventApi, element: HTMLElement): void {
        let calendarEvent = event.extendedProps.ce as CalendarEvent;
        if (calendarEvent?.id == null) {
            return;
        }

        let elements = this.eventElementsByCalendarEventId.get(calendarEvent.id);
        elements?.delete(element);
        if (elements?.size == 0) {
            this.eventElementsByCalendarEventId.delete(calendarEvent.id);
        }
    }

    private highlightLinkedEvents(event: EventApi): void {
        this.clearLinkedEventHighlights();

        let calendarEvent = event.extendedProps.ce as CalendarEvent;
        for (let eventId of this.getLinkedEventIds(calendarEvent)) {
            for (let element of this.eventElementsByCalendarEventId.get(eventId) ?? []) {
                element.classList.add("pp-linked-event-highlight");
            }
        }
    }

    private clearLinkedEventHighlights(): void {
        for (let elements of this.eventElementsByCalendarEventId.values()) {
            for (let element of elements) {
                element.classList.remove("pp-linked-event-highlight");
            }
        }
    }

    private getLinkedEventIds(event: CalendarEvent): number[] {
        if (event == null) {
            return [];
        }

        return Arrays.getUnique(Arrays.withoutNull([
            event.getStartRelativeToEventId(),
            ...(this.rawEventInput ?? []).filter(e => e.getStartRelativeToEventId() == event.id).map(e => e.id)
        ]));
    }
    updateResourceIds(): void {
        let events = this.fc.events as EventInput[];
        for (let e of events) {
            if (e.extendedProps.ce instanceof CalendarEvent) {
                this.setEventResourceId(e)
            }
        }

        this.updateEventOrder(events);  
        this.fc.events = [...events];
        this.fc.getApi().render();
        this.applyPendingCollapsedResourceKeys();
    }

    setEventResourceId(e: EventInput): void {
        let ce = e.extendedProps.ce as CalendarEvent;
        if (this.resourceType == "project") {
            let resourceIds = [
                Objects.toString(ce.projectId),
                ...Arrays.getSubListsFromBeginning(Strings.split(ce.structurePath, "|")).map(sp => Objects.toString(ce.projectId) + "^" + Arrays.itemsToString(sp, "|")),
                Objects.toString(ce.projectId) + "^" + ce.structurePath + "^" + ce.id
            ];
            e.resourceId = resourceIds.find(ri => this.collapsedResourceKeys.includes(ri)) ?? resourceIds[resourceIds.length - 1];
        }
        else {
            e.resourceId = Objects.toString((e.extendedProps.cei as CalendarEventInstance).calendarId);
        }
    }
    
    getCalendarColor(calendarId: number): number {
        let cal = this.service.calendars.find(c => c.id == calendarId);
        if (cal == null) {
            return 0;
        }
        else {
            switch (cal.type) {
                case CalendarType.user:
                    return this.service.users.find(r => r.calendarId == cal.id)?.color ?? 0;
                case CalendarType.role:
                    return this.service.roles.find(r => r.calendarId == cal.id)?.color ?? 0;
                case CalendarType.resource:
                    return this.service.resources.find(r => r.calendarId == cal.id)?.color ?? 0;
                default:
                    return 0;
            }
        }
    }

    openEventContextMenu(e: EventApi): void {
        if (e.display == "background") {
            //Background-Event, z.B. Feiertag
            return;
        }
        this.app.openContextMenu(this.service.getEventContextMenuItems(e.extendedProps.ce as CalendarEvent, this.service.getCurrentUser(), this.dataChanged, this.view));
    }

    openEmptySpaceContextMenu(date: Date, resourceId: string | null, event: MouseEvent): void {
        if (date == null) {
            return;
        }

        if (event != null) {
            this.app.setCurrentMousePos({ pageX: event.pageX, pageY: event.pageY });
        }
        let menu: MenuItem[] = [
            this.projects ? {
                label: this.app.t("entity.CalendarEvent.other.task"),
                icon: "fas fa-plus",
                command: async () => {
                    await this.app.dialog.component(EditCalendarEventForProjectComponent, this.app.t("ProjectPlanning.createTask"), null, {
                        id: 0,
                        item: this.createProjectEventForResource(resourceId, date)
                    });
                    await this.app.trackChanges();
                }
            } : null,
            !this.projects ?
            {
                label: this.app.t("entity.CalendarEvent.other.appointment"),
                icon: "fas fa-plus",
                command: async () => {
                    await this.app.dialog.component(EditCalendarEventAppointmentComponent, this.app.t("ProjectPlanning.createAppointment"), null, {
                        id: 0,
                        item: this.createAppointmentEventForResource(resourceId, date)
                    });
                    await this.app.trackChanges();
                }
            } : null
        ];
        this.app.openContextMenu(menu);
    }

    private createAppointmentEventForResource(resourceId: string | null, startDate: Date): CalendarEvent {
        let result = CalendarEvent.forProject(null, startDate);
        result.organisatorId = this.getUserIdFromCalendarResourceId(resourceId);
        return result;
    }

    private createProjectEventForResource(resourceId: string | null, startDate: Date): CalendarEvent {
        let result = CalendarEvent.forProject(resourceId != null ? this.getProjectFromResourceId(resourceId) : null, startDate);
        result.structurePath = this.getStructurePathFromResourceId(resourceId);
        return result;
    }
    private getUserIdFromCalendarResourceId(resourceId: string | null): number | null {
        if (this.resourceType != "calendar") {
            return null;
        }

        let calendarId = Utils.toNumber(resourceId);
        let calendar = this.service.calendars.find(c => c.id == calendarId);
        return calendar?.type == CalendarType.user ? this.service.users.find(u => u.calendarId == calendarId)?.id ?? null : null;
    }

    private getProjectFromResourceId(resourceId: string | null): Project | null {
        let projectId = Utils.toNumber(Strings.getSubstringTo(resourceId ?? "", "^", true));
        return this.service.projects?.find(p => p.id == projectId) ?? null;
    }
    private getStructurePathFromResourceId(resourceId: string | null): string | null {
        let parts = Strings.split(resourceId, "^");
        return parts.length >= 2 ? parts[1] : null;
    }
    private openEmptySpaceContextMenuFromResourceLane(event: MouseEvent): void {
        if (!this.resourceView || event.ctrlKey) {
            return;
        }

        const target = event.target as Element;
        if (this.isFullCalendarMenuBlockedTarget(target)) {
            return;
        }

        const resourceElement = this.getResourceElementFromCalendarElement(target);
        if (resourceElement == null || !this.isResourceTimelineLaneElement(resourceElement)) {
            return;
        }

        const resourceId = resourceElement.getAttribute("data-resource-id");
        const date = this.getDateFromCalendarElement(target, event);
        if (resourceId == null || date == null) {
            return;
        }

        event.preventDefault();
        event.stopPropagation();
        this.openEmptySpaceContextMenu(date, resourceId, event);
    }

    private isFullCalendarMenuBlockedTarget(target: Element): boolean {
        return target?.closest(".fc-event, .fc-popover, .fc-toolbar, .fc-button") != null;
    }

    private getResourceIdFromCalendarElement(target: Element): string | null {
        return this.getResourceElementFromCalendarElement(target)?.getAttribute("data-resource-id") ?? null;
    }

    private getResourceElementFromCalendarElement(target: Element): Element | null {
        return target?.closest("[data-resource-id]") ?? null;
    }

    private isResourceTimelineLaneElement(element: Element): boolean {
        return element.getAttribute("role") == "gridcell";
    }

    private getDateFromCalendarElement(target: Element, event?: MouseEvent): Date | null {
        let dateElement = target?.closest("[data-date]");
        if (dateElement != null) {
            return this.parseCalendarDateElement(dateElement);
        }

        if (event == null) {
            return null;
        }

        let dateElements = Array.from(this.fc.getApi().el.querySelectorAll("[data-date]")) as HTMLElement[];
        dateElement = dateElements.find(element => this.isElementAtMousePosition(element, event, true))
            ?? dateElements.find(element => this.isElementAtMousePosition(element, event, false))
            ?? null;

        return dateElement == null ? null : this.parseCalendarDateElement(dateElement);
    }

    private isElementAtMousePosition(element: HTMLElement, event: MouseEvent, checkY: boolean): boolean {
        let rect = element.getBoundingClientRect();
        if (rect.width <= 0 || rect.height <= 0) {
            return false;
        }

        let matchesX = event.clientX >= rect.left && event.clientX < rect.right;
        let matchesY = !checkY || (event.clientY >= rect.top && event.clientY <= rect.bottom);
        return matchesX && matchesY;
    }

    private parseCalendarDateElement(element: Element): Date | null {
        let value = element.getAttribute("data-date");
        if (value == null) {
            return null;
        }

        let parts = value.substring(0, 10).split("-");
        if (parts.length != 3) {
            return null;
        }

        let year = Number(parts[0]);
        let month = Number(parts[1]);
        let day = Number(parts[2]);
        if (!Number.isFinite(year) || !Number.isFinite(month) || !Number.isFinite(day)) {
            return null;
        }

        return new Date(year, month - 1, day);
    }

    jumpToToday(): void {
        if (this.resourceView) {
            this.scrollToToday();
        }
        else {
            this.fc.getApi().today();
        }
    }

    prev(): void {
        this.fc.getApi().prev();
    }

    next(): void {
        this.fc.getApi().next();
    }

    getTitle(): string {
        return this.fc?.getApi()?.view?.title ?? "";
    }

    scrollToToday(): void {   
        this.scrollToDate(Dates.startOf(new Date(), "week"), 0.25);
    }

    focusEvent(calendarEvent: CalendarEvent): void {
        this.ensureDateIsVisible(calendarEvent.startDate);
        this.scrollToDate(calendarEvent.startDate, 0.25);
        GuiUtils.angularTimerWithRetry(() => {
            let elements = this.eventElementsByCalendarEventId.get(calendarEvent.id);
            if (elements == null) {
                return;
            }
            for (let element of elements) {
                element.classList.add("pp-global-search-hit");
            }
            elements.values().next().value?.scrollIntoView({ behavior: "smooth", block: "center", inline: "center" });
            Utils.setTimerOnce(3_000, () => elements.forEach(element => element.classList.remove("pp-global-search-hit")));
        }, () => !Utils.isNoe(this.eventElementsByCalendarEventId.get(calendarEvent.id)), 100, 30);
    }

    private ensureDateIsVisible(date: Date): void {
        let year = date.getFullYear();
        if (year >= this.startYear && year <= this.endYear) {
            return;
        }

        this.startYear = year;
        this.endYear = year;
        let visibleRange = {
            start: year + "-01-01",
            end: year + 1 + "-01-01"
        };
        this.options.visibleRange = visibleRange;
        this.fc.getApi().setOption("visibleRange", visibleRange);
    }

    scrollToResourceEvent(resource: ResourceInput): void {    
        let event = Arrays.min((this.fc.events as EventInput[]).filter(e => e.resourceId == resource.id), e => (e.extendedProps.ce as CalendarEvent).startDate);
        if (event != null) {
            this.scrollToDate((event.extendedProps.ce as CalendarEvent).startDate);
        }
    }

    getScroller(): HTMLElement | null {
        return this.findHorizontalScroller(this.fc.getApi().el);
    }

    scrollToDate(date: Date, offset: boolean | number = false): void {
        let isoDate = Dates.toLocalIsoDateString(date).substring(0, 10); // YYYY-MM-DD
        let root = this.fc.getApi().el;
        let cells = Array.from(root.querySelectorAll(`[data-date^="${isoDate}"]`)) as HTMLElement[];
        let cell = cells.find(c => this.getScrollerForDateCell(c, root) != null) ?? cells[0];

        if (cell == null) {
            return;
        }

        let scroller = this.getScrollerForDateCell(cell, root);
        if (scroller == null) {
            return;
        }

        let scrollerRect = scroller.getBoundingClientRect();
        let cellRect = cell.getBoundingClientRect();

        let scrollOffset = typeof offset == "number" ? scroller.clientWidth * offset : offset ? scroller.clientWidth / 2 - cellRect.width / 2 : 0;
        let targetScrollLeft = scroller.scrollLeft + (cellRect.left - scrollerRect.left) - scrollOffset;

        scroller.scrollLeft = targetScrollLeft;
    }

    private getScrollerForDateCell(cell: HTMLElement, root: HTMLElement): HTMLElement | null {
        let current = cell.parentElement;
        while (current != null && current != root.parentElement) {
            if (this.isHorizontalScroller(current)) {
                return current;
            }

            current = current.parentElement;
        }

        return this.findHorizontalScroller(root);
    }

    private findHorizontalScroller(root: HTMLElement): HTMLElement | null {
        let elements = [root, ...Array.from(root.querySelectorAll("*"))] as HTMLElement[];
        return elements.find(e => this.isHorizontalScroller(e)) ?? null;
    }

    private isHorizontalScroller(element: HTMLElement): boolean {
        let style = window.getComputedStyle(element);
        return element.clientWidth > 0 && element.scrollWidth > element.clientWidth && style.overflowX != "visible" && style.overflowX != "clip";
    }

    
}
